// Google Analytics 4 tracking utilities

declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
    dataLayer?: any[];
  }
}

export const GA4_MEASUREMENT_ID = 'G-DSFVRM5R3G';

// Initialize GA4
export const initGA4 = () => {
  if (typeof window === 'undefined') return;
  
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer?.push(arguments);
  };
  
  window.gtag('js', new Date());
  window.gtag('config', GA4_MEASUREMENT_ID, {
    send_page_view: true,
    cookie_flags: 'SameSite=None;Secure',
  });
};

// Track custom events
export const trackGA4Event = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', eventName, eventParams);
};

// Specific tracking functions
export const trackPortfolioView = (params: {
  portfolio_item_id: string;
  hotel_title: string;
  location: string;
  stars: string;
  style: string;
  category: string;
}) => {
  trackGA4Event('portfolio_view', {
    portfolio_item_id: params.portfolio_item_id,
    hotel_title: params.hotel_title,
    location: params.location,
    stars: params.stars,
    style: params.style,
    category: params.category,
  });
};

export const trackImageClick = (params: {
  portfolio_item_id: string;
  hotel_title: string;
  location: string;
  image_path?: string;
}) => {
  trackGA4Event('image_click', {
    portfolio_item_id: params.portfolio_item_id,
    hotel_title: params.hotel_title,
    location: params.location,
    image_path: params.image_path || 'unknown',
  });
};

export const trackWebsiteClick = (params: {
  portfolio_item_id: string;
  hotel_title: string;
  location: string;
  website_url?: string;
}) => {
  trackGA4Event('website_click', {
    portfolio_item_id: params.portfolio_item_id,
    hotel_title: params.hotel_title,
    location: params.location,
    website_url: params.website_url || 'unknown',
  });
};

export const trackPhoneClick = (params: {
  portfolio_item_id: string;
  hotel_title: string;
  location: string;
}) => {
  trackGA4Event('phone_click', {
    portfolio_item_id: params.portfolio_item_id,
    hotel_title: params.hotel_title,
    location: params.location,
  });
};

export const trackEmailClick = (params: {
  portfolio_item_id: string;
  hotel_title: string;
  location: string;
}) => {
  trackGA4Event('email_click', {
    portfolio_item_id: params.portfolio_item_id,
    hotel_title: params.hotel_title,
    location: params.location,
  });
};

export const trackDetailsClick = (params: {
  portfolio_item_id: string;
  hotel_title: string;
  location: string;
}) => {
  trackGA4Event('details_click', {
    portfolio_item_id: params.portfolio_item_id,
    hotel_title: params.hotel_title,
    location: params.location,
  });
};

// CTA Tracking Functions
export const trackBookingCTAClick = (params: {
  page: string;
  cta_location: string;
}) => {
  trackGA4Event('booking_cta_click', {
    page: params.page,
    cta_location: params.cta_location,
  });
};

export const trackPortfolioCTAClick = (params: {
  page: string;
  cta_location: string;
}) => {
  trackGA4Event('portfolio_cta_click', {
    page: params.page,
    cta_location: params.cta_location,
  });
};

export const trackFloatingCTAClick = () => {
  trackGA4Event('floating_cta_click', {
    cta_type: 'floating_booking',
  });
};

export const trackServicesCTAClick = () => {
  trackGA4Event('services_cta_click', {
    page: 'services',
    cta_location: 'bottom',
  });
};

export const trackContactCTAClick = () => {
  trackGA4Event('contact_cta_click', {
    page: 'contact',
    cta_location: 'hero',
  });
};

export const trackFooterContactClick = (params: {
  contact_type: 'phone' | 'whatsapp' | 'email';
}) => {
  trackGA4Event('footer_contact_click', {
    contact_type: params.contact_type,
  });
};

export const trackNavigationClick = (params: {
  destination: string;
  source: string;
}) => {
  trackGA4Event('navigation_click', {
    destination: params.destination,
    source: params.source,
  });
};

